export type AccountAxis = {
  /** Topo do eixo Y — sempre um múltiplo "redondo" do incremento */
  max: number
  /** Incremento entre os ticks (RT03) */
  step: number
  /** Ticks de 0 até `max`, inclusive */
  ticks: number[]
}

/** Multiplicadores aceitos para o incremento, para o eixo cair sempre em valor legível e inteiro */
const NICE_STEPS = [1, 2, 5, 10]

/** Variante do eixo percentual: 2,5 é o que produz a escala 0 / 25% / 50% / 75% / 100% */
const NICE_STEPS_WITH_FRACTION = [1, 2, 2.5, 5, 10]

/** Número de intervalos desejados no eixo (4 intervalos = 5 ticks contando o 0) */
const TARGET_INTERVALS = 4

/**
 * RC02 / RT03: calcula um eixo Y independente para a conta.
 *
 * O máximo é arredondado para cima até o múltiplo do incremento "redondo" mais próximo, o que
 * garante a equiparação visual pedida em RC03: como o topo do eixo nunca fica muito acima do
 * maior valor da conta, a barra mais alta ocupa sempre uma fração parecida da altura do painel,
 * mesmo entre contas com ordens de grandeza diferentes.
 *
 * RN02: os valores são normalizados com `Math.abs` antes do cálculo — o eixo sempre começa em 0.
 *
 * `allowFractionalStep` só é usado pelo eixo percentual (GM); no eixo monetário o incremento é
 * mantido inteiro para o rótulo abreviado nunca exibir casas decimais.
 */
export const getAccountAxis = (values: number[], allowFractionalStep = false): AccountAxis => {
  const maxValue = Math.max(...values.map((value) => Math.abs(value)), 0)

  if (maxValue === 0) return { max: 1, step: 1, ticks: [0, 1] }

  const niceSteps = allowFractionalStep ? NICE_STEPS_WITH_FRACTION : NICE_STEPS
  const rawStep = maxValue / TARGET_INTERVALS
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const normalizedStep = rawStep / magnitude

  const step = (niceSteps.find((niceStep) => normalizedStep <= niceStep) ?? 10) * magnitude
  const intervals = Math.ceil(maxValue / step)
  const max = intervals * step

  const ticks = Array.from({ length: intervals + 1 }, (_, index) => index * step)

  return { max, step, ticks }
}
